import Layout from '@/components/layout/Layout';
import AppShellPending from '@/components/layout/AppShellPending';
import { SurveyGate } from '@/components/surveys/SurveyGate';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from 'sonner';
import { APIProvider } from '@vis.gl/react-google-maps';
import { useAuth } from '@/contexts/AuthContext';
import { OrganizationProvider } from '@/contexts/OrganizationContext';
import { TierIdentityLoader } from '@/contexts/TierIdentityLoader';
import { getCurrentOrganization } from '@/hooks/use-bss-organizations';
import { loadFeatureFlags } from '@/lib/feature-flags';
import { bustEntitlementCacheIfNeeded } from '@/lib/entitlement-cache-bust';
import { readSaleLink } from '@/lib/sale-link';
import { isOneTimeRoute } from '@/lib/one-time-routes';
import { isPublicEventSlugPath } from '@/lib/public-event-path';
import { supabase } from '@/lib/supabase';
import { getMyCampaignLanding, type CampaignLanding } from '@/api/link-courses';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import { useEffect } from 'react';
import { useDismissKeyboardOnReturn } from '@/hooks/useDismissKeyboardOnReturn';
import { RootErrorBoundary } from '@/components/RootErrorBoundary';
import { RootNotFound } from '@/components/RootNotFound';

interface RouterContext {
  queryClient: QueryClient;
}

// Routes that should not have the main layout. Matched as exact path or
// path prefix (so dynamic children like /mastermind/<id> are also bare).
const noLayoutRoutes = ['/verify', '/login', '/signup', '/forgot-password', '/reset-password', '/auth/confirm', '/auth/callback', '/invitations/accept', '/privacy', '/terms', '/data-deletion', '/mastermind', '/unsubscribe', '/rsvp'];

// Routes that don't require organization check
const noOrgCheckRoutes = ['/verify', '/login', '/signup', '/forgot-password', '/reset-password', '/auth/confirm', '/auth/callback', '/onboarding', '/invitations/accept', '/privacy', '/terms', '/data-deletion', '/unsubscribe', '/rsvp'];

// Routes that never require an authenticated session. Deliberately a
// SEPARATE list from `noLayoutRoutes` — the two questions ("does this route
// get the app shell?" and "does this route require auth?") are independent:
//   - '/onboarding' has the app shell AND requires auth — its own route's
//     beforeLoad enforces that on navigation-in, but beforeLoad does not
//     rerun while a route stays mounted. The reactive effect below is what
//     catches a session dying (another tab signs out, token refresh fails)
//     WHILE the onboarding wizard is still on screen, and it must keep
//     firing there regardless of how the layout lists change.
//   - '/mastermind' is layoutless AND genuinely reachable without a session,
//     but handles that itself (renders its own "Not signed in" state —
//     see routes/mastermind.$mastermindId.tsx) rather than via this redirect,
//     so it's listed here to preserve that existing behaviour.
//   - '/unsubscribe' is reached from a notification email, where the recipient
//     usually has no session in that browser. Its HMAC token is the credential,
//     so bouncing it to /login would break the one flow it exists for. It is on
//     all three lists: no shell, no org gate, no auth.
//   - '/rsvp' is the same shape: the two buttons in an event invitation email
//     land here, the signed token in the URL is the credential, and the
//     recipient usually has no session in the browser that opened the email.
//     On all three lists for the same reason as '/unsubscribe'.
//   - '/events/<slug>' (a slug, NOT a UUID) is the public ticket page for
//     outside buyers. It shares its prefix with the authed '/events' and
//     '/events/<uuid>' pages, so it cannot go on these prefix lists without
//     un-gating those too. `isPublicEventSlugPath` decides by shape instead and
//     is consulted alongside all three lists (see lib/public-event-path.ts).
const publicRoutes = ['/verify', '/login', '/signup', '/forgot-password', '/reset-password', '/auth/confirm', '/auth/callback', '/invitations/accept', '/privacy', '/terms', '/data-deletion', '/mastermind', '/unsubscribe', '/rsvp'];

function matchesRoute(pathname: string, routes: string[]): boolean {
  return routes.some((r) => pathname === r || pathname.startsWith(r + '/'));
}

/**
 * Whether `href` carries OAuth/auth-callback material — an implicit-flow
 * session in the hash (#access_token=...), a PKCE code (?code=...), or a
 * provider error. Mirrors supabase-js's own parseParametersFromURL: both the
 * query string and the hash fragment are checked, since GoTrue delivers
 * sessions in the hash but errors can arrive in either.
 *
 * Exported for the route-restore guard below: supabase-js consumes these
 * params ASYNCHRONOUSLY (URL parsing runs behind a Navigator LockManager
 * acquisition), so any synchronous history rewrite while they're still in the
 * URL destroys the session before it's ever read.
 */
export function hasAuthCallbackParams(href: string): boolean {
  const AUTH_KEYS = [
    'access_token',
    'refresh_token',
    'code',
    'error',
    'error_description',
    'error_code',
  ];
  const url = new URL(href);
  if (AUTH_KEYS.some((k) => url.searchParams.has(k))) return true;
  const hashParams = new URLSearchParams(
    url.hash.startsWith('#') ? url.hash.slice(1) : url.hash,
  );
  return AUTH_KEYS.some((k) => hashParams.has(k));
}

/**
 * Whether the reactive identity-loss guard (see RootComponent) should send
 * the viewer to /login. Pure + exported so it's unit-testable directly —
 * RootComponent pulls in the full app shell (Layout, OrganizationProvider,
 * Google Maps) which makes rendering it in isolation impractical, but the
 * actual decision here doesn't need any of that.
 */
export function shouldRedirectToLogin(pathname: string, hasUser: boolean): boolean {
  if (hasUser) return false;
  if (isPublicEventSlugPath(pathname)) return false;
  return !matchesRoute(pathname, publicRoutes);
}

/**
 * Whether the org guard should send the viewer to /onboarding. Exported so
 * this exact double-check policy is unit-testable against a real QueryClient
 * without needing a full TanStack Router beforeLoad context.
 *
 * Free-floor model: onboarding is profile completion, not org creation. The
 * viewer belongs on /onboarding when `onboarding_completed_at` is null (they
 * have not finished the wizard) OR they have no organisation (defensive — the
 * invited-but-not-accepted state, which the wizard's invitation branch
 * handles).
 *
 * The fast path (`ensureQueryData`) returns cached data if any exists,
 * regardless of staleness — instant for the common case (org present AND
 * onboarding already complete). Only when that comes back incomplete (no org
 * OR the flag still null) do we pay for a network round trip: a `fetchQuery`
 * with `staleTime: 0` forces an always-fresh read, and only ITS result
 * decides the redirect. This is what stops a stale/invalidated cached value
 * (e.g. from an org create, an invitation accept, or the completion step's
 * own invalidate, whose refetch hasn't landed yet) from bouncing a member who
 * is actually done.
 */
export async function shouldRedirectToOnboarding(queryClient: QueryClient): Promise<boolean> {
  const cached = await queryClient.ensureQueryData({
    queryKey: ['current-organization'],
    queryFn: getCurrentOrganization,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (cached.organization && cached.onboarding_completed_at) return false;

  const fresh = await queryClient.fetchQuery({
    queryKey: ['current-organization'],
    queryFn: getCurrentOrganization,
    staleTime: 0,
    retry: false,
  });

  return !fresh.organization || !fresh.onboarding_completed_at;
}

/** The Learn area: '/learn', '/learn/' and everything under '/learn/' — never '/learning'. */
export function isLearnPath(pathname: string): boolean {
  return pathname === '/learn' || pathname.startsWith('/learn/');
}

/** sessionStorage key for "this user has had their one landing in this tab". */
export function campaignLandedKey(userId: string): string {
  return `fb-campaign-landed:${userId}`;
}

/**
 * Where a person who signed up from a free-course link goes while they have no
 * business yet. Only called once shouldRedirectToOnboarding has said "not set up".
 *
 *   not a lead (or the lookup failed)      → '/onboarding' (unchanged behaviour)
 *   lead, first '/' in this tab            → their course, or '/learn' if it isn't available
 *   lead, on a Learn page                  → stay
 *   lead, anywhere else (Home included)    → '/onboarding'
 */
export type CampaignDecision =
  | { kind: 'course'; courseId: string }
  | { kind: 'learn' }
  | { kind: 'stay' }
  | { kind: 'onboarding' };

export function decideCampaignRoute(
  pathname: string,
  landing: CampaignLanding | null,
  alreadyLanded: boolean,
): CampaignDecision {
  if (!landing?.is_lead) return { kind: 'onboarding' };
  if (pathname === '/' && !alreadyLanded) {
    return landing.course_id ? { kind: 'course', courseId: landing.course_id } : { kind: 'learn' };
  }
  if (isLearnPath(pathname)) return { kind: 'stay' };
  return { kind: 'onboarding' };
}

/**
 * Is this navigation the next-move card's deep link, `/?move=<id>`?
 *
 * A notification or an email sends the builder to Home to answer one specific move (T11 D11),
 * and the card only exists on Home. Restoring the saved section on a fresh session would land
 * them on /learn with no card and no explanation, so the restore stands aside for this one URL
 * shape. Everything else about the restore — including its one-shot flag — is unchanged.
 */
export function isNextMoveDeepLink(pathname: string, search: unknown): boolean {
  if (pathname !== '/') return false;
  if (!search || typeof search !== 'object') return false;
  const { move } = search as { move?: unknown };
  return typeof move === 'string' && move.trim() !== '';
}

/**
 * The saved section to restore on a fresh tab, or null to stay on Home. A one-time link page
 * (/verify, …) saved by an older build is ignored: reopened without its link it is a dead end.
 */
export function routeToRestore(saved: string | null): string | null {
  if (!saved || saved === '/' || isOneTimeRoute(saved)) return null;
  return saved;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  pendingComponent: AppShellPending,
  pendingMs: 0,
  pendingMinMs: 0,
  beforeLoad: async ({ location, context: { queryClient } }) => {
    // Restore last visited section on PWA cold-start.
    // sessionStorage is cleared when the PWA relaunches (new session), so this
    // fires exactly once per launch at the start_url (/). Subsequent navigations
    // to / (user clicking Home) skip the redirect because the flag is already set.
    if (location.pathname === '/') {
      if (!sessionStorage.getItem('fb-route-restored')) {
        sessionStorage.setItem('fb-route-restored', '1');
        // OAuth returns land here with the session in the URL hash
        // (#access_token=... — implicit flow returns to origin + '/'). The
        // redirect below rewrites the URL via replaceState immediately, but
        // supabase-js reads that hash asynchronously — restoring here
        // destroyed the tokens before they were read, stranding the sign-in
        // back on /login (the retry then "worked" because this flag was set
        // by the failed attempt). Skip the restore for auth-callback URLs;
        // landing on Home right after sign-in is the expected destination
        // anyway. The flag above is still set so a later Home click doesn't
        // retroactively restore.
        // `/?move=<id>` is a deep link to the next-move card, which lives on Home: restoring
        // the saved section would drop the builder somewhere the move is not (Codex F6).
        if (
          !hasAuthCallbackParams(window.location.href) &&
          !isNextMoveDeepLink(location.pathname, location.search)
        ) {
          const saved = routeToRestore((() => { try { return localStorage.getItem('fb-last-route'); } catch { return null; } })());
          if (saved) {
            throw redirect({ to: saved, replace: true });
          }
        }
      }
    }

    // Skip org check for auth routes, onboarding itself, and the public
    // event ticket page (slug-shaped /events/<slug> only).
    if (
      noOrgCheckRoutes.some(route => location.pathname.startsWith(route)) ||
      isPublicEventSlugPath(location.pathname)
    ) {
      return;
    }

    // Sales in Alfie: a customer arriving from their sale link (/verify) pays first and does
    // onboarding after; their organisation was made by the sales page and is not set up yet.
    // Only /checkout, and only while this tab holds that link (lib/sale-link.ts).
    if (location.pathname === '/checkout' && readSaleLink()) {
      return;
    }

    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    // Populate the runtime feature-flag cache before any authed route paints,
    // so synchronous readers (resolveMediaUrl / media prefetch) see stable values
    // and don't churn the load-bearing player memo. Failures fall back to defaults.
    // Start the org request in parallel. A cache bust, when it fires, clears it
    // and shouldRedirectToOnboarding refetches.
    await Promise.all([
      loadFeatureFlags(queryClient),
      queryClient.prefetchQuery({
        queryKey: ['current-organization'],
        queryFn: getCurrentOrganization,
        staleTime: 5 * 60 * 1000,
        retry: false,
      }),
    ]);

    // Flag-gated: if entitlements changed server-side (a backfill, an admin
    // tier/add-on change), the persisted IndexedDB snapshot can lock a member
    // out of access they now have. Bumping `entitlement_cache_bust.value` in
    // admin/flags flushes that snapshot here so access self-heals on next load.
    await bustEntitlementCacheIfNeeded(queryClient);

    // Cache-aware org check — see shouldRedirectToOnboarding for the policy.
    try {
      if (await shouldRedirectToOnboarding(queryClient)) {
        // Free-course leads: a person who signed up from a course link and has no
        // business yet may use Learn and lands on their course once per tab.
        // Someone with a business who simply hasn't finished setup is not a lead.
        const org = queryClient.getQueryData<{ organization?: unknown }>(['current-organization']);
        if (!org?.organization) {
          const landing = await getMyCampaignLanding(queryClient, session.user.id);
          const key = campaignLandedKey(session.user.id);
          const landed = (() => { try { return !!sessionStorage.getItem(key); } catch { return false; } })();
          const decision = decideCampaignRoute(location.pathname, landing, landed);
          if (decision.kind === 'stay') return;
          if (decision.kind === 'course' || decision.kind === 'learn') {
            try { sessionStorage.setItem(key, '1'); } catch { /* private browsing */ }
            if (decision.kind === 'course') {
              throw redirect({ to: '/learn/courses/$courseId', params: { courseId: decision.courseId } });
            }
            throw redirect({ to: '/learn' });
          }
        }
        throw redirect({ to: '/onboarding' });
      }
    } catch (error) {
      // Re-throw TanStack Router redirects
      if (error instanceof Response) throw error;
      // Log other errors but don't block navigation
      console.error('[Root Guard] Failed to check organization', error);
    }
  },
  component: RootComponent,
  // Catches every ROUTER error — a render throw, a beforeLoad rejection here or
  // in any child route, a failed lazy-route import. The React <ErrorBoundary> in
  // main.tsx does NOT cover these: TanStack intercepts route errors before they
  // can reach React, so without this they hit TanStack's unstyled default with
  // no recovery and nothing logged.
  // It renders a page and LOGS; it deliberately does not reload. Recovery from a
  // failed chunk lives in one place, the vite:preloadError listener in main.tsx.
  errorComponent: RootErrorBoundary,
  // Unknown URLs (typos, stale bookmarks, the prototype-era /home) used to fall
  // through to TanStack's bare `<p>Not Found</p>` outside the shell. This
  // renders inside <Layout> via the root Outlet, so the member can carry on.
  notFoundComponent: RootNotFound,
});

function RootComponent() {

  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const showLayout =
    !noLayoutRoutes.some(
      (r) => location.pathname === r || location.pathname.startsWith(r + '/'),
    ) && !isPublicEventSlugPath(location.pathname);

  // Dismiss the on-screen keyboard whenever the PWA returns to the foreground.
  // Touch devices only — desktop focus is preserved across tab switches.
  useDismissKeyboardOnReturn();

  // Reactive redirect for auth state changes — e.g. the session expiring or
  // another tab signing out while this tab stays on a protected route.
  // Deliberately keyed off `publicRoutes` (via shouldRedirectToLogin), NOT
  // `showLayout`: a route can be layoutless yet still require auth (see
  // '/onboarding' — beforeLoad enforces auth on navigation-in, but doesn't
  // rerun while the route stays mounted, so this effect is what catches an
  // identity loss that happens while the wizard is already on screen).
  useEffect(() => {
    if (shouldRedirectToLogin(location.pathname, !!user)) {
      navigate({
        to: '/login',
        search: {
          redirect: location.href,
        },
      });
    }
  }, [user, location.pathname, navigate, location.href]);

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''}>
    <OrganizationProvider>
      {/* Loads data-driven tier badges/accents into the tier-config store. Headless. */}
      <TierIdentityLoader />
      {showLayout ? (
        <Layout>
          <Outlet />
          {/* Login-placement survey. Inside the showLayout branch on purpose: it
              must never fire on /login, /signup or the public pages. SurveyGate
              also guards on an authenticated user, and coordinates with the
              community placement so a member is asked at most once per session. */}
          <SurveyGate placement="login" />
        </Layout>
      ) : (
        <Outlet />
      )}
      <Toaster />
      <SonnerToaster
        position="top-right"
        offset={{ top: 'calc(4rem + env(safe-area-inset-top))', right: '1rem' }}
        // sonner v2 uses mobileOffset (not offset) on phone-width viewports; without
        // this the toast falls back to the ~16px default and lands under the iPhone
        // notch/status bar — unclickable on an installed PWA.
        mobileOffset={{ top: 'calc(4rem + env(safe-area-inset-top))', right: '1rem', left: '1rem' }}
      />
      {/* Voice dictation lives in the Alfie widget's composer
          (components/alfie-widget/WidgetComposer.tsx → use-dictation.ts). */}
      {/* bottom-LEFT: the devtools toggle is a floating disc in the same
          bottom-right corner as the Alfie launcher, and covered it in dev. */}
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-left" />}
    </OrganizationProvider>
    </APIProvider>
  );
}
